import React, { useEffect } from 'react';
import { S } from '@mobily/ts-belt';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { Box, Button, Grid, ModuleHeader, Placeholder } from '@stitch-fix/mode-react';
import type { BusinessLine } from '../../../../types/definitions/vendor/graphql-schema.d';
import { type GetUngatedPaginatedShopCategoryQuery, type ShopProductItemFragment, useGetUngatedPaginatedShopCategoryQuery } from './getUngatedPaginatedShopCategory';
import { type ShopCategoryGridPaginationProps, useShopCategoryGridPagination } from './useShopCategoryGridPagination';
import { useBuildShopProductItems } from './useBuildShopProductItems';
import { ModuleWrapper, type ModuleWrapperProps } from '../../../../components/ModuleWrapper';
import type { ModularComponentProps } from '../../../../components/ModularPageWrapper/ModularPageWrapper';
import type { ShopCategoryPromoCard } from '../PromoCard/PromoCard';
import { getShopProductSourceForType, type ShopProductSourceType } from './getShopProductSource';
export type { ShopCategoryPromoCard, Theme } from '../PromoCard/PromoCard';
export const SHOP_CATEGORY_PER_PAGE = 60;
export type ShopCategoryProps = {
  sourceType: ShopProductSourceType;
  categoryId: string;
  businessLine: BusinessLine;
  limit?: number;
  hideOutOfStockItems?: boolean;
  promoCards?: ShopCategoryPromoCard[];
} & Omit<ModuleWrapperProps, 'children' | 'alignment'>;
export { ShopCategoryGridCursorProvider, type ShopCategoryGridPaginationProps } from './useShopCategoryGridPagination';
const ShopCategoryGrid = ({
  gridItems
}: {
  gridItems: React.ReactElement[];
}) => <Box px={1} py={{
  sm: 1.5,
  md: 2
}}>
    <Grid columns={{
    sm: 2,
    md: 3,
    lg: 4
  }} spacing={{
    sm: [2, 1],
    md: [2, 1.5]
  }}>
      {gridItems}
    </Grid>
  </Box>;
const LoadingShopCategory = ({
  limit
}: {
  limit: number;
}) => {
  // match placeholder count to the product count being requested so the height doesn't jump when loaded
  const placeholders = Array.from({
    length: limit
  }, (_, index) => <Box data-testid="loading-placeholder" key={`placeholder-${index}`}>
      <Placeholder variant="rect" width="100%" height={0} pt="125%" />
      <Placeholder width={4} mt={1} />
      <Placeholder width={8} mt={0.25} />
      <Placeholder width={2} mt={0.25} />
    </Box>);
  return <ShopCategoryGrid gridItems={placeholders} />;
};
const EmptyShopCategory = ({
  categoryId
}: {
  categoryId: string;
}) => {
  const {
    ref,
    EventProvider
  } = useViewEvent({
    schema: 'category_view',
    name: 'empty_category_recommendations',
    category_id: categoryId
  });
  return <EventProvider>
      <Box px={1} py={3} style={{
      textAlign: 'center'
    }} ref={ref}>
        <ModuleHeader header="These popular picks are unavailable" subheader="We'll try to restock with fresh selects soon, check back again later." />
      </Box>
    </EventProvider>;
};
const ShopProductCards = ({
  categoryId,
  pageSize,
  shopProducts,
  promoCards,
  themeId
}: {
  categoryId: string;
  pageSize: number;
  shopProducts: ShopProductItemFragment[];
  promoCards?: ShopCategoryPromoCard[];
  themeId?: string;
}) => {
  const {
    EventProvider,
    ref
  } = useViewEvent({
    category_id: categoryId,
    name: 'grid',
    schema: 'category_view'
  });
  const {
    shopCategory
  } = useBuildShopProductItems({
    shopProducts,
    promoCards,
    pageSize,
    themeId
  });
  return <EventProvider>
      <Box ref={ref}>
        <ShopCategoryGrid gridItems={shopCategory} />
      </Box>
    </EventProvider>;
};

// fetchMore replaces the connection by default
// append nodes and keep the latest pageInfo
const mergeShopProductPages = (previousData: GetUngatedPaginatedShopCategoryQuery, nextData: GetUngatedPaginatedShopCategoryQuery, sourceType: ShopProductSourceType) => {
  const previousSource = getShopProductSourceForType(previousData, sourceType);
  const nextSource = getShopProductSourceForType(nextData, sourceType);
  if (!previousSource || !nextSource) {
    // keep showing previous data when either response is still incomplete.
    return previousData;
  }
  const mergedConnection = {
    ...nextSource.connection,
    nodes: [...previousSource.connection.nodes, ...nextSource.connection.nodes]
  };
  if (sourceType === 'theme') {
    const fixTheme = previousData.fixTheme;
    if (!fixTheme) {
      return previousData;
    }
    return {
      ...previousData,
      fixTheme: {
        ...fixTheme,
        shopProducts: mergedConnection
      }
    };
  }
  const shopContent = previousData.shopContent;
  const categoryPage = shopContent?.categoryPage;
  const category = categoryPage?.category;
  if (!shopContent || !categoryPage || !category) {
    return previousData;
  }
  return {
    ...previousData,
    shopContent: {
      ...shopContent,
      categoryPage: {
        ...categoryPage,
        category: {
          ...category,
          shopProducts: mergedConnection
        }
      }
    }
  };
};
export const ShopCategory = (props: ShopCategoryProps & ModularComponentProps) => {
  const {
    categoryId,
    sourceType,
    businessLine,
    limit = SHOP_CATEGORY_PER_PAGE,
    hideOutOfStockItems = false,
    verticalSpacing,
    moduleTitle,
    moduleFooter,
    promoCards
  } = props;
  const {
    gridId,
    previousGridId,
    showMoreEnabled = false
  } = props as ShopCategoryProps & ShopCategoryGridPaginationProps;
  const {
    paginationCursor,
    hasNoMorePreviousGridProducts,
    isLoadingMore,
    isWaitingForPreviousGridCursor,
    setCursorFromData,
    setIsLoadingMore
  } = useShopCategoryGridPagination({
    sourceId: categoryId,
    sourceType,
    gridId,
    previousGridId
  });

  // skip fetching when there is no category, a chained upstream grid has not
  // published its cursor yet, or there are no more products to fetch
  const shouldSkipQuery = S.isEmpty(categoryId) || isWaitingForPreviousGridCursor || hasNoMorePreviousGridProducts;
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetUngatedPaginatedShopCategoryQuery({
    // each grid manages cursor chaining locally, so avoid cache reuse across inserted grids
    fetchPolicy: 'no-cache',
    variables: {
      businessLine,
      after: paginationCursor,
      first: limit,
      id: categoryId,
      includeCategory: sourceType === 'category',
      includeTheme: sourceType === 'theme',
      hideOutOfStockItems
    },
    skip: shouldSkipQuery,
    context: {
      operationTelemetry: {
        tags: {
          business_line: businessLine
        }
      }
    }
  });
  useEffect(() => {
    if (error) {
      setCursorFromData();
      return;
    }

    // publish the current grid cursor for downstream grids
    if (data) setCursorFromData(data);
  }, [data, error, setCursorFromData]);
  const shopProductSource = getShopProductSourceForType(data, sourceType);
  const shopProductConnection = shopProductSource?.connection;
  const shopProducts = shopProductConnection?.nodes ?? [];
  const visibleShopProducts = hideOutOfStockItems ? shopProducts.filter(shopProduct => shopProduct.colorOptions.length > 0) : shopProducts;
  const pageInfo = shopProductConnection?.pageInfo;
  const nextPageCursor = pageInfo?.endCursor;

  // a valid next cursor is required before Show More can request another page
  const canFetchNextPage = Boolean(pageInfo?.hasNextPage && nextPageCursor);
  const handleShowMoreClick = async () => {
    if (!canFetchNextPage || !nextPageCursor) {
      return;
    }

    // use local loading state because Apollo's initial loading flag does not cover this button state
    setIsLoadingMore(true);
    try {
      // fetch the next cursor page and merge it into the current grid
      const result = await fetchMore({
        variables: {
          businessLine,
          after: nextPageCursor,
          first: limit,
          id: categoryId || '',
          includeCategory: sourceType === 'category',
          includeTheme: sourceType === 'theme',
          hideOutOfStockItems
        },
        updateQuery: (previousData, {
          fetchMoreResult
        }) => mergeShopProductPages(previousData, fetchMoreResult, sourceType)
      });

      // publish the updated end cursor for downstream grids after the next page is merged
      setCursorFromData(result.data);
    } catch {
      // keep the current data and cursor so the user can retry the same page
    } finally {
      setIsLoadingMore(false);
    }
  };
  const showMoreButton = showMoreEnabled && canFetchNextPage ? <Box display="flex" style={{
    justifyContent: 'center'
  }} pt={1} pb={2}>
        <Button disabled={isLoadingMore || !canFetchNextPage} onClick={handleShowMoreClick} variant="outline-large">
          Show More
        </Button>
      </Box> : null;
  const renderContent = () => {
    if (loading || isWaitingForPreviousGridCursor) {
      // chained grids show placeholders while the previous grid determines their starting cursor
      return <LoadingShopCategory limit={limit} />;
    }
    if (error || hasNoMorePreviousGridProducts || shopProducts.length === 0) {
      // empty includes true empty data, failed requests, and exhausted previous-grid chains
      return <EmptyShopCategory categoryId={categoryId} />;
    }
    if (visibleShopProducts.length === 0) {
      // preserve access to later pages when the current page only contains hidden products.
      return <>
          <EmptyShopCategory categoryId={categoryId} />
          {showMoreButton}
        </>;
    }
    return <>
        <ShopProductCards categoryId={categoryId} pageSize={limit} shopProducts={visibleShopProducts} promoCards={promoCards} themeId={shopProductSource?.kind === 'theme' ? shopProductSource.themeId : undefined} />
        {showMoreButton}
      </>;
  };
  return <Box mt={verticalSpacing}>
      <ModuleWrapper alignment="center" moduleTitle={moduleTitle} moduleFooter={moduleFooter}>
        <Box maxWidth="1200px" mx="auto">
          {renderContent()}
        </Box>
      </ModuleWrapper>
    </Box>;
};